import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as echarts from 'echarts';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiTrendingUp, FiActivity, FiCalendar } = FiIcons;

const HappinessHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (user?.teamId) {
      loadHistoryData();
    }
    
    // Cleanup chart on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [user]);

  const loadHistoryData = async () => {
    try {
      setLoading(true);
      
      // Calculate date 30 days ago
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const startDateString = startDate.toISOString().split('T')[0];

      // 1. Get Team Members
      const teamRelations = await apiService.getUserTeamRelations({ 
        team_id: user.teamId,
        limit: 1000 
      });
      
      const memberIds = teamRelations.data 
        ? teamRelations.data.map(m => m.user_id) 
        : [];
      
      if (user.id && !memberIds.includes(parseInt(user.id))) {
        memberIds.push(parseInt(user.id));
      }

      if (memberIds.length === 0) {
        setLoading(false);
        return;
      }

      // 2. Fetch Happiness Indices for the last 30 days
      // Using [gte] operator as per API spec
      const happinessResponse = await apiService.getHappinessIndices({
        'evaluation_date[gte]': startDateString,
        limit: 1000
      });

      const allRecords = happinessResponse.data || [];
      
      // Filter for team members
      const teamRecords = allRecords.filter(r => memberIds.includes(r.user_id));

      // 3. Process Data for Chart
      processChartData(teamRecords, startDate, endDate);

    } catch (error) {
      console.error('Error loading happiness history:', error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (records, startDate, endDate) => {
    const dates = [];
    const teamData = [];
    const userData = [];
    
    // Generate array of date strings for the range
    let currentDate = new Date(startDate);
    
    // Ensure endDate includes the full day by comparing dates properly or setting time to 0
    // Simple comparison loop
    const endDateTime = new Date(endDate).setHours(0,0,0,0);
    
    // Reset currentDate time to 0 to avoid infinite loops if times differ
    currentDate.setHours(0,0,0,0);

    while (currentDate <= endDateTime) {
      // Format as YYYY-MM-DD using local time or UTC consistently. 
      // Using simple string manipulation to avoid timezone shifts on simple dates
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Group records by date
    const recordsByDate = {};
    records.forEach(r => {
      // Taking only the date part YYYY-MM-DD
      const dateKey = r.evaluation_date.split('T')[0];
      if (!recordsByDate[dateKey]) {
        recordsByDate[dateKey] = [];
      }
      recordsByDate[dateKey].push(r);
    });

    // Variables to hold the last known values (Forward Fill)
    let lastTeamAvg = null;
    let lastUserVal = null;

    // Calculate averages per day
    dates.forEach(date => {
      const daysRecords = recordsByDate[date] || [];
      
      // Team Average (including user)
      if (daysRecords.length > 0) {
        const sum = daysRecords.reduce((acc, curr) => acc + curr.value, 0);
        const avg = parseFloat((sum / daysRecords.length).toFixed(1));
        teamData.push(avg);
        lastTeamAvg = avg; // Update last known value
      } else {
        // Use last known value if available (Forward Fill)
        teamData.push(lastTeamAvg);
      }

      // User Rating
      const myRecord = daysRecords.find(r => r.user_id === parseInt(user.id));
      if (myRecord) {
        userData.push(myRecord.value);
        lastUserVal = myRecord.value; // Update last known value
      } else {
        // Use last known value if available (Forward Fill)
        userData.push(lastUserVal);
      }
    });

    renderChart(dates, teamData, userData);
  };

  const renderChart = (dates, teamData, userData) => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: {
          color: '#1f2937'
        }
      },
      legend: {
        data: ['Team Durchschnitt', 'Meine Stimmung'],
        bottom: 0,
        selectedMode: true
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: {
          lineStyle: {
            color: '#9ca3af'
          }
        },
        axisLabel: {
          color: '#6b7280',
          formatter: (value) => {
            const date = new Date(value);
            return `${date.getDate()}.${date.getMonth() + 1}.`;
          }
        }
      },
      yAxis: {
        type: 'value',
        min: 1,
        max: 7,
        splitNumber: 6,
        axisLabel: {
          color: '#6b7280'
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6'
          }
        }
      },
      series: [
        {
          name: 'Team Durchschnitt',
          type: 'line',
          data: teamData,
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 3,
            color: '#152627' 
          },
          itemStyle: {
            color: '#152627'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(21, 38, 39, 0.2)' },
              { offset: 1, color: 'rgba(21, 38, 39, 0)' }
            ])
          },
          connectNulls: true
        },
        {
          name: 'Meine Stimmung',
          type: 'line',
          data: userData,
          smooth: true,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: '#87ff8f', // brand-secondary
            type: 'dashed'
          },
          itemStyle: {
            color: '#87ff8f',
            borderColor: '#fff',
            borderWidth: 2
          },
          connectNulls: true
        }
      ]
    };

    chartInstance.current.setOption(option);

    const handleResize = () => {
      chartInstance.current && chartInstance.current.resize();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/dashboard')}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-brand-dark flex items-center">
              <SafeIcon icon={FiActivity} className="w-6 h-6 mr-2 text-brand-secondary" />
              Stimmungsverlauf
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Entwicklung der Team-Stimmung über die letzten 30 Tage
            </p>
          </div>
        </div>
        
        <div className="hidden sm:flex items-center bg-gray-50 px-3 py-1.5 rounded-md text-xs text-gray-500 border border-gray-100">
          <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-2" />
          Letzte 30 Tage
        </div>
      </div>

      {/* Chart Content */}
      <div className="flex-grow p-6 relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        )}
        
        <div ref={chartRef} className="w-full h-full min-h-[350px]" />
        
        {!loading && (
           <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-gray-500 bg-gray-50 p-4 rounded-lg">
              <div className="flex items-start">
                 <div className="w-3 h-3 rounded-full bg-brand-dark mt-0.5 mr-2 flex-shrink-0"></div>
                 <p>
                   <strong>Team Durchschnitt:</strong> Zeigt den durchschnittlichen Happiness-Score aller Teammitglieder. Werte werden fortgeschrieben, bis neue Bewertungen vorliegen.
                 </p>
              </div>
              <div className="flex items-start">
                 <div className="w-3 h-3 rounded-full bg-brand-secondary mt-0.5 mr-2 flex-shrink-0 border border-brand-dark"></div>
                 <p>
                   <strong>Meine Stimmung:</strong> Ihre persönlichen Bewertungen. Werte werden fortgeschrieben, bis Sie eine neue Bewertung abgeben.
                 </p>
              </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default HappinessHistory;